import moment from 'moment';
import { pathOr } from 'ramda';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { PartThumbnail } from '@frontend/components';
import { useDateFormat } from '@frontend/utils';

import MentionsDisplay from 'components/lib2/MentionsDisplay';

import { getUserId } from 'store/session/selectors';

import { toMoment } from 'utils/dateUtils';

import { TASK_STATUSES } from '../data/constants';

const AssignedBy = styled.p`
  color: #666666;
  font-size: 12px;
`;

const CommentRow = styled.div`
  color: #666666;
  display: flex;
  font-size: 12px;

  p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const CompleteLabel = styled.span`
  color: #00bc32;
`;

const ContainerLink = styled(Link)`
  background-color: #fff;
  border-bottom: solid 1px #ebebeb;
  cursor: pointer;
  display: flex;
  transition: 0.2s ease all;

  :hover {
    background-color: #f4f8fb;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 8px;
  width: 100%;
`;

const DueLabel = styled.p`
  color: #666666;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  padding-right: 5px;
`;

const Filename = styled.span`
  color: #666666;
  font-family: 'Roboto Mono', monospace;
  padding: 0 5px;
`;

const OverdueLabel = styled.span`
  color: #f71c1c;
`;

const PartNumberDetail = styled.span`
  font-family: 'Roboto Mono', monospace;
`;

const PartNumberInfo = styled.p`
  color: #666666;
  font-weight: 600;
`;

const PartNumberLabel = styled.span`
  padding-right: 5px;
  text-transform: uppercase;

  :not(:first-of-type) {
    padding-left: 5px;
  }
`;

const PartNumberRow = styled.div`
  align-items: baseline;
  display: flex;
  font-size: 12px;
`;

const QuoteLink = styled(Link)`
  color: #0066ff;
  cursor: pointer;
  flex-shrink: 0;
  outline: none;

  :hover {
    text-decoration: underline;
  }
`;

const StatusRow = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const ThumbnailContainer = styled.div`
  align-items: center;
  background: linear-gradient(
    150deg,
    rgb(255, 255, 255) 0%,
    rgb(204, 204, 204) 100%
  );
  border-right: 1px solid rgb(238, 238, 238);
  display: flex;
  min-width: 100px;
  position: relative;
  overflow: hidden;
  width: 100px;
`;

const TaskListItem = ({ task }) => {
  const userId = useSelector(getUserId);
  let link = null;
  if (task?.part || task?.annotation?.commentedOnQuoteItemId) {
    link = pathOr(false, ['annotation', 'commentedOnQuoteItemId'], task)
      ? `/quotes/edit/${pathOr(
          false,
          ['annotation', 'commentedOnQuoteNumberWithRevision'],
          task,
        )}?quote_item=${pathOr(
          '',
          ['annotation', 'commentedOnQuoteItemId'],
          task,
        )}`
      : `/parts/viewer/${pathOr(
          '',
          ['part', 'uuid'],
          task,
        )}?annotation_id=${pathOr('', ['annotation', 'id'], task)}`;
  }

  const { toNumericalEventDateTimeString } = useDateFormat();

  return (
    <ContainerLink to={link}>
      <ThumbnailContainer>
        {link ? (
          <PartThumbnail
            containsVirus={pathOr(false, ['part', 'containsVirus'], task)}
            filename={pathOr('', ['part', 'filename'], task)}
            partFileUuid={pathOr('', ['part', 'primaryFileUuid'], task)}
          />
        ) : null}
      </ThumbnailContainer>
      <ContentContainer>
        <PartNumberRow>
          <PartNumberInfo>
            <PartNumberLabel>PART</PartNumberLabel>
            <PartNumberDetail>
              {pathOr('--', ['part', 'partNumber'], task)}
            </PartNumberDetail>
            <PartNumberLabel>REV</PartNumberLabel>
            <PartNumberDetail>
              {pathOr('--', ['part', 'revision'], task)}
            </PartNumberDetail>
          </PartNumberInfo>
          <Filename>{pathOr('', ['part', 'filename'], task)}</Filename>
          {task.quoteItemComponents.length > 0 && (
            <>
              {task.quoteItemComponents.map(
                (quoteItemComponent, index, array) => {
                  if (index == 0) {
                    return (
                      <QuoteLink
                        key={quoteItemComponent.quoteItemId}
                        to={`/quotes/edit/${quoteItemComponent.quoteNumberWithRevision}?quote_item=${quoteItemComponent.quoteItemId}`}
                      >
                        Relates to Quote{`${array.length > 1 ? 's' : ''}`} #{' '}
                        {quoteItemComponent.quoteNumberWithRevision}
                        {`${index != array.length - 1 ? ',' : ''}`}
                      </QuoteLink>
                    );
                  } else {
                    return (
                      <QuoteLink
                        key={quoteItemComponent.quoteItemId}
                        to={`/quotes/edit/${quoteItemComponent.quoteNumberWithRevision}?quote_item=${quoteItemComponent.quoteItemId}`}
                      >
                        &nbsp;
                        {quoteItemComponent.quoteNumberWithRevision}
                        {`${index != array.length - 1 ? ',' : ''}`}
                      </QuoteLink>
                    );
                  }
                },
              )}
            </>
          )}
        </PartNumberRow>
        <CommentRow>
          &quot;
          <MentionsDisplay
            content={pathOr([], ['annotation', 'comments', 0, 'content'], task)}
          />
          &quot;
        </CommentRow>
        <StatusRow>
          <DueLabel>
            {task.status == TASK_STATUSES.RESOLVED ? (
              <CompleteLabel>
                Completed at {toNumericalEventDateTimeString(task.resolvedDate)}
              </CompleteLabel>
            ) : toMoment(task.dueDate) < moment() ? (
              <OverdueLabel>
                Overdue since {toNumericalEventDateTimeString(task.dueDate)}
              </OverdueLabel>
            ) : (
              <span>Due {toNumericalEventDateTimeString(task.dueDate)}</span>
            )}
          </DueLabel>
          <AssignedBy>
            Assigned to&nbsp;
            {task.assignedToId == userId ? 'you' : task.assignedToFullName}
            &nbsp;by&nbsp;
            {task.createdById == userId ? 'you' : task.createdByFullName}
          </AssignedBy>
        </StatusRow>
      </ContentContainer>
    </ContainerLink>
  );
};

export default TaskListItem;
